import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import RefreshIcon from "@material-ui/icons/Refresh";
import clsx from "clsx";

const useStyles = makeStyles((_theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const msg = defineMessages({
  contentTitle: {
    id: "errorBoundaryFallback.content.title",
    description: "Text of the content title.",
    defaultMessage: "Sorry, something went wrong...",
  },
  contentBody: {
    id: "errorBoundaryFallback.content.body",
    description: "Text of the content body.",
    defaultMessage:
      "Try reloading the page. If the problem persists, contact the administrator.",
  },
  reloadButtonLabel: {
    id: "errorBoundaryFallback.reloadButton.label",
    description: "Label of the reload button.",
    defaultMessage: "Reload",
  },
});

export type ErrorBoundaryFallbackProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

function ErrorBoundaryFallback(props: ErrorBoundaryFallbackProps) {
  const { className, ...other } = props;
  const classes = useStyles();
  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);

  const handleReloadButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      document.location.reload();
    },
    []
  );

  return (
    <div className={rootClasses} {...other}>
      <Typography variant="h4" gutterBottom>
        <FormattedMessage {...msg.contentTitle} />
      </Typography>
      <Typography gutterBottom>
        <FormattedMessage {...msg.contentBody} />
      </Typography>
      <Button
        color="primary"
        variant="contained"
        startIcon={<RefreshIcon />}
        onClick={handleReloadButtonClick}
      >
        <FormattedMessage {...msg.reloadButtonLabel} />
      </Button>
    </div>
  );
}

export default ErrorBoundaryFallback;
